import * as React from 'react';
import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  Slider,
  FormHelperText,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  Landscape as SceneIcon,
  LocationOn as LocationIcon,
  WbSunny as AtmosphereIcon,
  DirectionsRun as ActionIcon,
  Hearing as SensoryIcon,
  People as CharactersIcon,
  AccessTime as TimeIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Refresh as RefineIcon,
  ExpandMore as ExpandIcon,
  Timeline as TimelineIcon,
  Chat as DialogueIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { SceneService } from '../services/sceneService';
import { SceneSession, SceneDetails } from '../models/scene';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

export const SceneAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [mainTabValue, setMainTabValue] = useState(0);
  const [sceneTabValue, setSceneTabValue] = useState(0);
  const [sessions, setSessions] = useState<SceneSession[]>([]);
  const [currentSession, setCurrentSession] = useState<SceneSession | null>(null);
  const [sceneConcept, setSceneConcept] = useState('');
  const [genre, setGenre] = useState('dramatic');
  const [style, setStyle] = useState('cinematic');
  const [moodAndTone, setMoodAndTone] = useState('');
  const [feedback, setFeedback] = useState('');
  const [aspectToRefine, setAspectToRefine] = useState<string>('all');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupExpanded, setSetupExpanded] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (currentSession && setupExpanded) {
      setSetupExpanded(false);
    }
  }, [currentSession]);

  const loadSessions = () => {
    const allSessions = SceneService.getAllSessions();
    setSessions(allSessions);
  };

  const handleStartSession = async () => {
    if (!sceneConcept.trim()) {
      setError('Please enter a scene concept');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionName = `Scene-${Date.now()}`;
      const response = await SceneService.startSession({
        SessionName: sessionName,
        InitialPrompt: sceneConcept,
        genre,
        style,
        moodAndTone: moodAndTone || undefined,
        temperature,
        maxTokens,
      });

      const session = SceneService.createLocalSession(
        response.sessionId,
        sceneConcept,
        response.scene,
        sessionName
      );

      setCurrentSession(session);
      loadSessions();
      setSceneConcept('');
      setMoodAndTone('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create scene');
    } finally {
      setLoading(false);
    }
  };

  const handleIterate = async () => {
    if (!currentSession || !feedback.trim()) {
      setError('Please provide feedback for refinement');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await SceneService.iterate(currentSession.sessionId, {
        feedback,
        aspectToRefine: aspectToRefine === 'all' ? undefined : aspectToRefine as any,
        temperature,
        maxTokens,
      });

      SceneService.addHistory(
        currentSession.id,
        response.scene,
        feedback,
        response.changes
      );

      setCurrentSession(SceneService.getSession(currentSession.id));
      setFeedback('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to refine scene');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: SceneSession) => {
    setCurrentSession(session);
    setMainTabValue(0); // Switch to Create tab
  };

  const handleDeleteSession = (id: string) => {
    SceneService.deleteSession(id);
    if (currentSession?.id === id) {
      setCurrentSession(null);
    }
    loadSessions();
  };

  const handleExport = () => {
    if (!currentSession) return;

    const content = SceneService.exportScene(currentSession);
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scene-${currentSession.currentScene.title.toLowerCase().replace(/\s+/g, '-')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderSceneDetails = (scene: SceneDetails) => {
    return (
      <Box>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          <SceneIcon color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }} />
          {scene.title}
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Setting */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  <LocationIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> Setting
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  <strong>Location:</strong> {scene.setting.location}
                </Typography>
                {scene.setting.period && (
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Period:</strong> {scene.setting.period}
                  </Typography>
                )}
                <Typography 
                  paragraph 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {scene.setting.environment}
                </Typography>
                {scene.setting.layout && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    <strong>Layout:</strong><br />{scene.setting.layout}
                  </Typography>
                )}
                {scene.setting.importantObjects && scene.setting.importantObjects.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? 'Objects:' : 'Important Objects:'}
                    </Typography>
                    {scene.setting.importantObjects.map((obj, i) => (
                      <Chip 
                        key={i} 
                        label={obj} 
                        size="small" 
                        variant="outlined" 
                        sx={{ 
                          mr: 0.5, 
                          mb: 0.5,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }} 
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Atmosphere */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  <AtmosphereIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> Atmosphere
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  <strong>Mood:</strong> {scene.atmosphere.mood}
                </Typography>
                <Typography 
                  paragraph 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {scene.atmosphere.overallFeeling}
                </Typography>
                {scene.atmosphere.tension && (
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Tension:</strong> {scene.atmosphere.tension}
                  </Typography>
                )}
                {scene.atmosphere.lightingDescription && (
                  <Typography sx={{ mt: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Lighting:</strong> {scene.atmosphere.lightingDescription}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Time and Weather */}
          {scene.timeAndWeather && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.125rem' }
                    }}
                  >
                    <TimeIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> {isMobile ? 'Time' : 'Time & Weather'}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <strong>Time:</strong> {scene.timeAndWeather.timeOfDay}
                  </Typography>
                  {scene.timeAndWeather.weather && (
                    <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <strong>Weather:</strong> {scene.timeAndWeather.weather}
                    </Typography>
                  )}
                  {scene.timeAndWeather.season && (
                    <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <strong>Season:</strong> {scene.timeAndWeather.season}
                    </Typography>
                  )}
                  {scene.timeAndWeather.lightingConditions && (
                    <Typography sx={{ mt: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {scene.timeAndWeather.lightingConditions}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Characters */}
          {scene.characters && scene.characters.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.125rem' }
                    }}
                  >
                    <CharactersIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> Characters
                  </Typography>
                  {scene.characters.map((char, i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Typography 
                        variant="subtitle2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {char.name} ({char.role})
                      </Typography>
                      {char.position && (
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {isMobile ? 'Pos' : 'Position'}: {char.position}
                        </Typography>
                      )}
                      {char.action && (
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          Action: {char.action}
                        </Typography>
                      )}
                      {char.emotion && (
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          Emotion: {char.emotion}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Action */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  <ActionIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> Action
                </Typography>
                <Typography 
                  paragraph
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {scene.action.mainAction}
                </Typography>
                {scene.action.conflictPresent && (
                  <Alert 
                    severity="warning" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    <strong>Conflict:</strong> {scene.action.conflictPresent}
                  </Alert>
                )}
                {scene.action.movementAndBlocking && (
                  <Typography 
                    paragraph
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <strong>{isMobile ? 'Movement:' : 'Movement & Blocking:'}</strong><br />
                    {scene.action.movementAndBlocking}
                  </Typography>
                )}
                {scene.action.backgroundActivity && scene.action.backgroundActivity.length > 0 && (
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? 'Background:' : 'Background Activity:'}
                    </Typography>
                    {scene.action.backgroundActivity.map((activity, i) => (
                      <Typography 
                        key={i} 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        • {activity}
                      </Typography>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sensory Details */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  <SensoryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> {isMobile ? 'Sensory' : 'Sensory Details'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={scene.sensoryDetails.auditory ? 6 : 12}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Visual
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {scene.sensoryDetails.visual}
                    </Typography>
                  </Grid>
                  {scene.sensoryDetails.auditory && (
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Auditory
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {scene.sensoryDetails.auditory}
                      </Typography>
                    </Grid>
                  )}
                  {scene.sensoryDetails.olfactory && (
                    <Grid item xs={12} md={4}>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Olfactory
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {scene.sensoryDetails.olfactory}
                      </Typography>
                    </Grid>
                  )}
                  {scene.sensoryDetails.tactile && (
                    <Grid item xs={12} md={4}>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Tactile
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {scene.sensoryDetails.tactile}
                      </Typography>
                    </Grid>
                  )}
                  {scene.sensoryDetails.taste && (
                    <Grid item xs={12} md={4}>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Taste
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {scene.sensoryDetails.taste}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Dialogue */}
          {scene.dialogue && scene.dialogue.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.125rem' }
                    }}
                  >
                    <DialogueIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> Dialogue
                  </Typography>
                  {scene.dialogue.map((d, i) => (
                    <Box 
                      key={i} 
                      sx={{ 
                        mb: 2, 
                        p: { xs: 1.5, sm: 2 }, 
                        bgcolor: 'background.default', 
                        borderRadius: 1 
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        color="primary"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {d.speaker}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          my: 1,
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        "{d.text}"
                      </Typography>
                      {d.subtext && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            fontStyle: 'italic',
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}
                        >
                          Subtext: {d.subtext}
                        </Typography>
                      )}
                      {d.deliveryNotes && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          Delivery: {d.deliveryNotes}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Additional Details */}
          {(scene.emotionalTone || scene.pacing || scene.keyElements?.length) && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'Details' : 'Additional Details'}
                  </Typography>
                  {scene.emotionalTone && (
                    <Typography 
                      paragraph
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <strong>Emotional Tone:</strong> {scene.emotionalTone}
                    </Typography>
                  )}
                  {scene.pacing && (
                    <Typography 
                      paragraph
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <strong>Pacing:</strong> {scene.pacing}
                    </Typography>
                  )}
                  {scene.keyElements && scene.keyElements.length > 0 && (
                    <Box>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Key Elements:
                      </Typography>
                      {scene.keyElements.map((element, i) => (
                        <Chip 
                          key={i} 
                          label={element} 
                          color="secondary" 
                          sx={{ 
                            mr: 0.5, 
                            mb: 0.5,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }} 
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 2 }, 
            mb: 2,
            flexWrap: 'wrap'
          }}>
            <SceneIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}
            >
              {isMobile ? 'Scene Agent' : 'Scene Visionary Agent'}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Paper 
          elevation={0}
          sx={{ 
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Tabs 
              value={mainTabValue} 
              onChange={(_, newValue) => setMainTabValue(newValue)}
              sx={{ 
                mb: 2,
                '& .MuiTab-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 48
                }
              }}
              variant={isMobile ? 'fullWidth' : 'standard'}
            >
              <Tab label="Create" />
              <Tab label="History" />
            </Tabs>

            <TabPanel value={mainTabValue} index={0}>
              <Paper sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}>
                <Box 
                  sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => setSetupExpanded(!setupExpanded)}
                >
                  <Typography 
                    variant="h6"
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'Setup' : 'Scene Setup'}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ minWidth: 44, minHeight: 44 }}
                    aria-label={setupExpanded ? 'Collapse setup' : 'Expand setup'}
                  >
                    {setupExpanded ? <ExpandLessIcon /> : <ExpandIcon />}
                  </IconButton>
                </Box>
                
                <Collapse in={setupExpanded}>
                  <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>

                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid item xs={12} md={6}>
                        {!currentSession ? (
                          <>
                            <TextField
                              fullWidth
                              multiline
                              rows={isMobile ? 3 : 4}
                              label={isMobile ? 'Concept' : 'Scene Concept'}
                              value={sceneConcept}
                              onChange={(e) => setSceneConcept(e.target.value)}
                              placeholder={isMobile ? 'Describe scene...' : 'Describe the scene you want to create...'}
                              sx={{ mb: 2 }}
                              InputProps={{
                                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                              }}
                              InputLabelProps={{
                                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                              }}
                            />

                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Genre
                                  </InputLabel>
                                  <Select 
                                    value={genre} 
                                    onChange={(e) => setGenre(e.target.value)} 
                                    label="Genre"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                  >
                                    <MenuItem value="dramatic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Dramatic</MenuItem>
                                    <MenuItem value="action" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Action</MenuItem>
                                    <MenuItem value="romantic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Romantic</MenuItem>
                                    <MenuItem value="horror" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Horror</MenuItem>
                                    <MenuItem value="comedy" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Comedy</MenuItem>
                                    <MenuItem value="thriller" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Thriller</MenuItem>
                                    <MenuItem value="fantasy" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Fantasy</MenuItem>
                                    <MenuItem value="scifi" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Sci-Fi</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Style
                                  </InputLabel>
                                  <Select 
                                    value={style} 
                                    onChange={(e) => setStyle(e.target.value)} 
                                    label="Style"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                  >
                                    <MenuItem value="cinematic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Cinematic</MenuItem>
                                    <MenuItem value="theatrical" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Theatrical</MenuItem>
                                    <MenuItem value="literary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Literary</MenuItem>
                                    <MenuItem value="minimalist" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Minimalist</MenuItem>
                                    <MenuItem value="detailed" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{isMobile ? 'Detailed' : 'Highly Detailed'}</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>

                            <TextField
                              fullWidth
                              label={isMobile ? 'Mood (optional)' : 'Mood and Tone (optional)'}
                              value={moodAndTone}
                              onChange={(e) => setMoodAndTone(e.target.value)}
                              placeholder={isMobile ? 'e.g., tense, melancholic...' : 'e.g., tense, melancholic, uplifting...'}
                              sx={{ mt: 2, mb: 2 }}
                              InputProps={{
                                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                              }}
                              InputLabelProps={{
                                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                              }}
                            />

                            <Button
                              variant="contained"
                              fullWidth
                              onClick={handleStartSession}
                              disabled={!sceneConcept.trim() || loading}
                              startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                              sx={{ minHeight: 44 }}
                            >
                              {loading ? (isMobile ? 'Creating...' : 'Creating Scene...') : 'Create Scene'}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Alert 
                              severity="info" 
                              sx={{ 
                                mb: 2,
                                fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                              }}
                            >
                              {isMobile ? `ID: ${currentSession.id}` : `Session: ${currentSession.id}`}
                            </Alert>

                            <TextField
                              fullWidth
                              multiline
                              rows={isMobile ? 2 : 3}
                              label={isMobile ? 'Feedback' : 'Refinement Feedback'}
                              value={feedback}
                              onChange={(e) => setFeedback(e.target.value)}
                              placeholder={isMobile ? 'What to change?' : 'What would you like to change or enhance?'}
                              sx={{ mb: 2 }}
                              InputProps={{
                                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                              }}
                              InputLabelProps={{
                                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                              }}
                            />

                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <Typography 
                                gutterBottom
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                              >
                                Aspect to Refine
                              </Typography>
                              <RadioGroup value={aspectToRefine} onChange={(e) => setAspectToRefine(e.target.value)}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <FormControlLabel 
                                      value="all" 
                                      control={<Radio size="small" />} 
                                      label="All Aspects"
                                      sx={{
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }
                                      }}
                                    />
                                    <FormControlLabel 
                                      value="setting" 
                                      control={<Radio size="small" />} 
                                      label="Setting"
                                      sx={{
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }
                                      }}
                                    />
                                    <FormControlLabel 
                                      value="atmosphere" 
                                      control={<Radio size="small" />} 
                                      label="Atmosphere"
                                      sx={{
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <FormControlLabel 
                                      value="action" 
                                      control={<Radio size="small" />} 
                                      label="Action"
                                      sx={{
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }
                                      }}
                                    />
                                    <FormControlLabel 
                                      value="dialogue" 
                                      control={<Radio size="small" />} 
                                      label="Dialogue"
                                      sx={{
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }
                                      }}
                                    />
                                    <FormControlLabel 
                                      value="sensory" 
                                      control={<Radio size="small" />} 
                                      label="Sensory"
                                      sx={{
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </RadioGroup>
                            </FormControl>

                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={handleIterate}
                                  disabled={!feedback.trim() || loading}
                                  startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <RefineIcon />)}
                                  sx={{ minHeight: 44 }}
                                >
                                  {loading ? 'Refining...' : 'Refine'}
                                </Button>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => setCurrentSession(null)}
                                  sx={{ minHeight: 44 }}
                                >
                                  New Scene
                                </Button>
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={6}>

                        <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

                        <Typography 
                          variant="subtitle2" 
                          gutterBottom
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          Model Settings
                        </Typography>

                        <FormControl fullWidth sx={{ mb: 1 }}>
                          <Typography 
                            variant="body2" 
                            gutterBottom
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                          >
                            {isMobile ? 'Temp' : 'Temperature'}: {temperature}
                          </Typography>
                          <Slider
                            value={temperature}
                            onChange={(_, val) => setTemperature(val as number)}
                            min={0}
                            max={1}
                            step={0.1}
                            valueLabelDisplay="auto"
                            size="small"
                          />
                          <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {isMobile ? '0 = focused, 1 = creative' : 'Controls creativity: 0 = focused, 1 = creative'}
                          </FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                          <Typography 
                            variant="body2" 
                            gutterBottom
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                          >
                            {isMobile ? 'Tokens' : 'Max Tokens'}: {maxTokens}
                          </Typography>
                          <Slider
                            value={maxTokens}
                            onChange={(_, val) => setMaxTokens(val as number)}
                            min={512}
                            max={4096}
                            step={256}
                            valueLabelDisplay="auto"
                            size="small"
                          />
                          <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {isMobile ? 'Response length' : 'Controls response length'}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>

              <Paper sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}>
                {currentSession ? (
                  <>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2,
                      flexWrap: 'wrap',
                      gap: 1
                    }}>
                      <Tabs 
                        value={sceneTabValue} 
                        onChange={(_, val) => setSceneTabValue(val)}
                        variant={isMobile ? 'fullWidth' : 'standard'}
                        sx={{
                          '& .MuiTab-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 48
                          }
                        }}
                      >
                        <Tab label={isMobile ? 'Current' : 'Current Scene'} />
                        <Tab label={isMobile ? 'History' : 'Development History'} />
                      </Tabs>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          onClick={handleExport} 
                          title="Export Scene" 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <TabPanel value={sceneTabValue} index={0}>
                      {currentSession.currentScene ? (
                        renderSceneDetails(currentSession.currentScene)
                      ) : (
                        <Typography 
                          variant="body1" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          No scene data available
                        </Typography>
                      )}
                    </TabPanel>

                    <TabPanel value={sceneTabValue} index={1}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'History' : 'Development History'}
                      </Typography>
                      {currentSession.history.map((h, index) => (
                        <Accordion key={index} defaultExpanded={index === currentSession.history.length - 1}>
                          <AccordionSummary expandIcon={<ExpandIcon />}>
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {index === 0 ? 'Initial' : `Iteration ${index}`} - {isMobile 
                                ? new Date(h.timestamp).toLocaleDateString() 
                                : new Date(h.timestamp).toLocaleString()}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {index === 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                  {isMobile ? 'Concept:' : 'Original Concept:'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontStyle: 'italic',
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                  }}
                                >
                                  "{currentSession.initialConcept}"
                                </Typography>
                              </Box>
                            )}
                            {h.feedback && (
                              <Box sx={{ mb: 2 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                  Feedback:
                                </Typography>
                                <Typography 
                                  variant="body2"
                                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                  {h.feedback}
                                </Typography>
                              </Box>
                            )}
                            {h.changes && h.changes.length > 0 && (
                              <Box>
                                <Typography 
                                  variant="subtitle2" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                  {isMobile ? 'Changes:' : 'Changes Made:'}
                                </Typography>
                                {h.changes.map((c, i) => (
                                  <Typography 
                                    key={i} 
                                    variant="body2"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                  >
                                    • {c}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </TabPanel>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                    <SceneIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
                    <Typography 
                      variant="h6" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      {isMobile ? 'Create a scene' : 'Create a scene to get started'}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </TabPanel>

            <TabPanel value={mainTabValue} index={1}>
              {sessions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                  <TimelineIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                  <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'No sessions' : 'No scene sessions yet'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {isMobile ? 'Create your first scene' : 'Create your first scene to begin'}
                  </Typography>
                </Box>
              ) : (
                <List>
                  {sessions.map((session) => (
                    <React.Fragment key={session.id}>
                      <ListItem
                        sx={{ 
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': { backgroundColor: 'action.hover' },
                          pr: isMobile ? 10 : 14
                        }}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                          <SceneIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </Avatar>
                        <ListItemText
                          primary={session.currentScene?.title || 'Untitled Scene'}
                          secondary={
                            <>
                              <Typography 
                                component="span" 
                                variant="body2" 
                                color="text.primary"
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                              >
                                {isMobile 
                                  ? `${session.history?.length || 0} iter.`
                                  : `${session.currentScene?.setting?.location || 'Unknown location'} • ${session.history?.length || 0} iterations`
                                }
                              </Typography>
                              <br />
                              <Typography 
                                component="span" 
                                variant="caption"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              >
                                {isMobile 
                                  ? new Date(session.createdAt).toLocaleDateString()
                                  : `Created: ${new Date(session.createdAt).toLocaleString()}`
                                }
                              </Typography>
                            </>
                          }
                          primaryTypographyProps={{
                            sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                          }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            onClick={() => handleLoadSession(session)}
                            title="Load Session"
                            sx={{ mr: 1, minWidth: 40, minHeight: 40 }}
                            size="small"
                          >
                            <PlayIcon />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDeleteSession(session.id)}
                            title="Delete Session"
                            sx={{ minWidth: 40, minHeight: 40 }}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </TabPanel>
          </Box>
        </Paper>

      </Box>
    </Container>
  );
};